import {
    Box,
    Typography,
    Button,
    IconButton,
    ButtonGroup,
    Tooltip,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    OpenInNew,
    ZoomIn,
    ZoomOut,
    FitScreen,
    RotateLeft,
    RotateRight,
    Layers,
    LayersOutlined,
} from '@mui/icons-material';

interface PdfToolbarProps {
    isMobile: boolean;
    viewAllPages: boolean;
    selectedFormType: string;
    filteredPagesLength: number;
    currentPageIndex: number;
    isCurrentPageRemoved: boolean;
    hasCurrentPageData: boolean;
    zoomPercent: number;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onFitWidth: () => void;
    onPreviousPage: () => void;
    onNextPage: () => void;
    onOpenPdf: () => void;
    onRotateLeft: () => void;
    onRotateRight: () => void;
    rotateAllPages: boolean;
    onToggleRotateAllPages: () => void;
}

const PdfToolbar = ({
    isMobile,
    viewAllPages,
    selectedFormType,
    filteredPagesLength,
    currentPageIndex,
    isCurrentPageRemoved,
    hasCurrentPageData,
    zoomPercent,
    onZoomIn,
    onZoomOut,
    onFitWidth,
    onPreviousPage,
    onNextPage,
    onOpenPdf,
    onRotateLeft,
    onRotateRight,
    rotateAllPages,
    onToggleRotateAllPages,
}: PdfToolbarProps) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
            {viewAllPages
                ? 'All PDF Pages'
                : selectedFormType
                  ? `${selectedFormType.toUpperCase()} Pages`
                  : 'Select a form type'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 2 }}>
            {/* Zoom Controls */}
            {filteredPagesLength > 0 && (
                <ButtonGroup
                    size="small"
                    variant="outlined"
                >
                    <Button
                        onClick={onZoomOut}
                        title="Zoom Out"
                    >
                        <ZoomOut fontSize="small" />
                    </Button>
                    {!isMobile && (
                        <Button
                            onClick={onFitWidth}
                            disabled={zoomPercent === 100}
                            title="Reset zoom"
                        >
                            {zoomPercent}%
                        </Button>
                    )}
                    <Button
                        onClick={onZoomIn}
                        title="Zoom In"
                    >
                        <ZoomIn fontSize="small" />
                    </Button>
                    <Button
                        onClick={onFitWidth}
                        title="Fit to width"
                    >
                        <FitScreen fontSize="small" />
                    </Button>
                </ButtonGroup>
            )}
            {/* Rotation Controls */}
            {filteredPagesLength > 0 && (
                <ButtonGroup
                    size="small"
                    variant="outlined"
                >
                    <Tooltip title="Rotate left (Shift+←)">
                        <Button onClick={onRotateLeft}>
                            <RotateLeft fontSize="small" />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Rotate right (Shift+→)">
                        <Button onClick={onRotateRight}>
                            <RotateRight fontSize="small" />
                        </Button>
                    </Tooltip>
                    <Tooltip title={rotateAllPages ? "Rotation applies to all pages" : "Rotation applies to this page only"}>
                        <Button
                            onClick={onToggleRotateAllPages}
                            color={rotateAllPages ? "primary" : "inherit"}
                        >
                            {rotateAllPages ? <Layers fontSize="small" /> : <LayersOutlined fontSize="small" />}
                        </Button>
                    </Tooltip>
                </ButtonGroup>
            )}
            {/* Page Navigation */}
            {filteredPagesLength > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        onClick={onPreviousPage}
                        disabled={currentPageIndex === 0}
                        size="small"
                    >
                        <ChevronLeft />
                    </IconButton>
                    <Typography
                        variant="body2"
                        sx={
                            isCurrentPageRemoved
                                ? { textDecoration: 'line-through', color: 'text.disabled' }
                                : undefined
                        }
                    >
                        Page {currentPageIndex + 1} of {filteredPagesLength}
                        {isCurrentPageRemoved ? ' (Removed)' : ''}
                        {!hasCurrentPageData
                            && !isCurrentPageRemoved
                            && selectedFormType
                            && currentPageIndex < filteredPagesLength && (
                                <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ color: 'warning.main', fontWeight: 600, ml: 0.5 }}
                                >
                                    (No Extraction)
                                </Typography>
                            )}
                    </Typography>
                    <IconButton
                        onClick={onNextPage}
                        disabled={currentPageIndex >= filteredPagesLength - 1}
                        size="small"
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
            )}
            {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ButtonGroup
                        size="small"
                        variant="outlined"
                    >
                        <Button
                            onClick={onOpenPdf}
                            title="Open PDF"
                        >
                            <OpenInNew fontSize="small" />
                        </Button>
                    </ButtonGroup>
                </Box>
            )}
        </Box>
    </Box>
);

export default PdfToolbar;
